
import { FormSearchPerson, LocaleStrings, UpdateSearchPerson } from '@/types/paramModels';
import { computed, defineComponent, onBeforeMount, PropType, ref, watch } from 'vue'
import { useStore } from 'vuex';
import { format } from "date-fns";

import EasyDrop from '@/components/EasyDrop.vue';
import EasyDobPicker from '@/components/Booking/PersonalData/EasyDobPicker.vue';
import { SearchCountry, SearchGender } from '@/types/models';

export default defineComponent({
    name: "InfoForm",
    components: {
        EasyDrop,
        EasyDobPicker
    },
    emits: ['setCurrentPersonValid'],
    props: {
        person: {
            type: Object as PropType<FormSearchPerson>,
            required: true
        },
        adultsCount: {
            type: Number,
            required: true
        },
        childrenCount: {
            type: Number,
            required: true
        },
        isContactPerson: {
            type: Boolean
        },
        countries: {
            type: Array as PropType<Array<SearchCountry>>,
            required: true
        },
        genders: {
            type: Array as PropType<Array<SearchGender>>,
            required: true
        },
        pageTexts: {
            type: Object,
            required: true
        },
        localeStrings: {
            type: Object as PropType<LocaleStrings>,
            required: true
        },
        validateAllFields: {
            type: Boolean
        }
    },
    setup(_props, { emit }) {
        const store = useStore();
        const emailDirty = ref(false);

        const infoForm = ref();

        const emailHasError = ref(false);

        const errorMessage = (fieldName: string) => {
            return _props.pageTexts['hotelPageGeneralFormValidationFieldIsRequired'].replace(/\[[field\]]+\]/g, fieldName);
        }

        const dob = computed(() => _props.person.searchPerson.birthdate as string);
        const searchPerson = computed(() => _props.person.searchPerson);

        const isEmailValid = (email) => {
            if (!emailDirty.value) return (true);

            if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(email)) {
                return (true)
            }
            return (false)
        }

        const isPersonValid = () => {
            if (_props.isContactPerson) {
                return searchPerson.value.firstName
                    && searchPerson.value.lastName
                    && searchPerson.value.gender
                    && searchPerson.value.birthdate
                    && searchPerson.value.street
                    && searchPerson.value.postal
                    && searchPerson.value.city
                    && searchPerson.value.country
                    && searchPerson.value.gender
                    && searchPerson.value.phone
                    && searchPerson.value.email;
            }
            return searchPerson.value.firstName
                && searchPerson.value.lastName
                && searchPerson.value.gender
                && searchPerson.value.birthdate;
        }

        const updateCurrentPersonValid = () => {
            emit("setCurrentPersonValid", isPersonValid());
        }
        const countryButtonValue = computed(() => searchPerson.value.country?.name);
        const genderButtonValue = computed(() => searchPerson.value.gender?.name);

        const updateEmail = (event: Event) => {
            if (!isEmailValid(searchPerson.value.email)) {
                emailHasError.value = true;
                document.getElementById("email")?.classList.add('has-error');
                return false;
            }
            document.getElementById("email")?.classList.remove('has-error');
            emailHasError.value = false;
            emailDirty.value = true;
            updatePerson(event);
        }

        const updatePerson = (event: Event) => {
            const input = event.target as HTMLInputElement;
            const value = input.value;
            const name = input.name;
            const params = { hotelRoomIndex: _props.person.hotelRoomIndex, personIndex: _props.person.personIndex, propName: name, value: value, isAdult: _props.person.isAdult } as UpdateSearchPerson;
            store.dispatch("TravelSearchBookingModule/updatePersonProp", params).then(() => {
                updateCurrentPersonValid();
            });
            if (!value && input.required) {
                input.closest('.standard-input')?.classList.add('has-error');
            } else {
                input.closest('.standard-input')?.classList.remove('has-error');
            }
        }

        const updateGender = (gender: SearchGender) => {
            document.getElementById('personGender')?.classList.remove('has-error');
            const params = { hotelRoomIndex: _props.person.hotelRoomIndex, personIndex: _props.person.personIndex, propName: "gender", value: gender, isAdult: _props.person.isAdult } as UpdateSearchPerson;
            store.dispatch("TravelSearchBookingModule/updatePersonProp", params).then(() => {
                updateCurrentPersonValid();
            });

        }

        const updateDob = (dob: Date) => {
            document.getElementById('personDOB')?.classList.remove('has-error');
            const birthDate = format(dob, "yyyy-MM-dd");
            const params = { hotelRoomIndex: _props.person.hotelRoomIndex, personIndex: _props.person.personIndex, propName: "birthdate", value: birthDate, isAdult: _props.person.isAdult } as UpdateSearchPerson;
            store.dispatch("TravelSearchBookingModule/updatePersonProp", params).then(() => {
                updateCurrentPersonValid();
            });
        }

        const updateCountry = (country: SearchCountry) => {
            document.getElementById('countryWrapper')?.classList.remove('has-error');
            store.dispatch("TravelSearchBookingModule/updateContactPersonCountry", country).then(() => {
                updateCurrentPersonValid();
            });
        }

        onBeforeMount(() => {
            updateCurrentPersonValid();
            if (_props.isContactPerson && _props.countries.length === 1) {
                store.dispatch("TravelSearchBookingModule/updateContactPersonCountry", _props.countries[0]);
            }
        })

        watch(() => _props.validateAllFields, () => {
            if (_props.validateAllFields) {
                if (!isPersonValid()) {
                    const form = infoForm.value as HTMLFormElement;
                    const textInputs = form.querySelectorAll('input[type=text]');
                    if (textInputs) {
                        for (const input of textInputs) {
                            const changeEvent = new Event('change');
                            input.dispatchEvent(changeEvent);
                        }
                    }
                    if (!searchPerson.value.gender) {
                        document.getElementById('personGender')?.classList.add('has-error');
                    }
                    if (!searchPerson.value.birthdate) {
                        document.getElementById('personDOB')?.classList.add('has-error');
                    }
                    if (_props.isContactPerson && !searchPerson.value.email) {
                        document.getElementById('email')?.classList.add('has-error');
                    }
                    if (_props.isContactPerson && !searchPerson.value.country) {
                        document.getElementById('countryWrapper')?.classList.add('has-error');
                    }
                }
            }
        });

        return {
            dob,
            searchPerson,
            updatePerson,
            updateDob,
            emailHasError,
            countryButtonValue,
            genderButtonValue,
            updateCountry,
            updateEmail,
            updateGender,
            errorMessage,
            infoForm
        }
    },
})
